import { type FullAirHandlerUnit } from "@eisox/api/core-api";
import { cn, Radio } from "@eisox/design-system";

import * as DropdownMenu from "~/components/dropdown-menu";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { compareAirHandlerUnitNames } from "../helpers/air-handler-unit.helper";
import {
  getKeyLabel,
  getNodeLabel,
  isBranchSelected,
  isNestedNode,
  isPointSelected,
  isUndisplayedKey,
  MAX_HISTORY_SELECTIONS,
  remapObjectWithSortedKeys,
  resolveEntityId,
  toggleSelection,
  type HistoryPointSelection,
} from "../helpers/data-selector.helper";

interface RecursiveDropdownProps {
  obj: unknown;
  entityId: string;
  selected: HistoryPointSelection[];
  onChange: (selected: HistoryPointSelection[]) => void;
  parentKey?: string;
}

const RecursiveDropdown: React.FC<RecursiveDropdownProps> = ({ obj, entityId, selected, onChange, parentKey }) => {
  if (obj === null || typeof obj !== "object") return null;

  const entries = Array.isArray(obj) ? Object.entries(obj) : Object.entries(remapObjectWithSortedKeys(obj));
  const maxReached = selected.length >= MAX_HISTORY_SELECTIONS;

  return (
    <>
      {entries.map(([key, value]) => {
        if (isNestedNode(key, value)) {
          const childEntityId = Array.isArray(value) ? entityId : resolveEntityId(value, entityId);

          if (Array.isArray(value)) {
            return (
              <DropdownMenu.Sub key={key}>
                <DropdownMenu.SubTrigger
                  className={cn(isBranchSelected(value, entityId, selected, true) && "text-primary-blue")}
                >
                  {getKeyLabel(key)}
                </DropdownMenu.SubTrigger>
                <DropdownMenu.SubContent>
                  {value.map((item, index) => {
                    const itemEntityId = resolveEntityId(item, entityId);
                    return (
                      <DropdownMenu.Sub key={itemEntityId}>
                        <DropdownMenu.SubTrigger
                          className={cn(isBranchSelected(item, itemEntityId, selected) && "text-primary-blue")}
                        >
                          {getNodeLabel(String(index), item, key)}
                        </DropdownMenu.SubTrigger>
                        <DropdownMenu.SubContent>
                          <RecursiveDropdown
                            obj={item}
                            entityId={itemEntityId}
                            selected={selected}
                            onChange={onChange}
                            parentKey={key}
                          />
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Sub>
                    );
                  })}
                </DropdownMenu.SubContent>
              </DropdownMenu.Sub>
            );
          }

          return (
            <DropdownMenu.Sub key={key}>
              <DropdownMenu.SubTrigger
                className={cn(isBranchSelected(value, childEntityId, selected) && "text-primary-blue")}
              >
                {getNodeLabel(key, value, parentKey)}
              </DropdownMenu.SubTrigger>
              <DropdownMenu.SubContent>
                <RecursiveDropdown
                  obj={value}
                  entityId={childEntityId}
                  selected={selected}
                  onChange={onChange}
                  parentKey={key}
                />
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
          );
        }

        if (isUndisplayedKey(key) || key === "name") return null;

        const checked = isPointSelected(selected, entityId, key);
        const itemDisabled = maxReached && !checked;

        return (
          <Tooltip
            key={key}
            content={m.consumption_max_series_tooltip({ count: MAX_HISTORY_SELECTIONS })}
            openingCondition={itemDisabled}
          >
            <DropdownMenu.CheckboxItem
              checked={checked}
              onCheckedChange={() => onChange(toggleSelection(selected, entityId, key))}
              disabled={itemDisabled}
              className={cn(
                "data-disabled:pointer-events-auto data-disabled:cursor-default data-disabled:text-primary-darker-gray",
                checked && "text-primary-blue",
              )}
            >
              <Radio checked={checked} multiple disabled={itemDisabled} aria-hidden tabIndex={-1} />
              {getKeyLabel(key)}
            </DropdownMenu.CheckboxItem>
          </Tooltip>
        );
      })}
    </>
  );
};

interface AirHandlerUnitDataSelectorProps {
  airHandlerUnits: FullAirHandlerUnit[];
  selected: HistoryPointSelection[];
  onChange: (selected: HistoryPointSelection[]) => void;
  disabled?: boolean;
}

export const AirHandlerUnitDataSelector: React.FC<AirHandlerUnitDataSelectorProps> = ({
  airHandlerUnits,
  selected,
  onChange,
  disabled = false,
}) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger disabled={disabled}>
      {m.ahu_history_selector_trigger({ count: selected.length })}
    </DropdownMenu.Trigger>
    <DropdownMenu.Content>
      {[...airHandlerUnits]
        .sort((a, b) => compareAirHandlerUnitNames(a.name, b.name))
        .map(airHandlerUnit => (
          <DropdownMenu.Sub key={airHandlerUnit.id}>
            <DropdownMenu.SubTrigger
              className={cn(isBranchSelected(airHandlerUnit, airHandlerUnit.id, selected) && "text-primary-blue")}
            >
              {airHandlerUnit.name}
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent>
              <RecursiveDropdown
                obj={airHandlerUnit}
                entityId={airHandlerUnit.id}
                selected={selected}
                onChange={onChange}
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        ))}
    </DropdownMenu.Content>
  </DropdownMenu.Root>
);

export type { AirHandlerUnitDataSelectorProps, HistoryPointSelection };
